/**
 * Business Integration Template Types
 *
 * MOVED TO DATA-DRIVEN ARCHITECTURE
 *
 * This file previously contained hardcoded business integration templates
 * which violated FERP's data-driven architecture principles.
 *
 * Templates now come from backend APIs only.
 * Use: BusinessIntegrationService to fetch templates from backend
 */

import { Integration, Resource, WorkflowDefinition, Policy } from '../types-16-complete';

// Template parameter types for customization
export interface TemplateParameter {
  name: string;
  displayName: string;
  type: 'string' | 'select' | 'multi-select' | 'number' | 'boolean' | 'url';
  default?: any;
  options?: string[];
  validation?: string;
  description: string;
  required?: boolean;
}

// Business template definition (type definition only - data comes from backend)
export interface BusinessIntegrationTemplate {
  id: string;
  name: string;
  businessFriendlyName: string;
  description: string;
  category: 'Payroll Systems' | 'Content Management' | 'Financial Processing' | 'Healthcare Systems' | 'Custom';
  icon: string;
  complexity: 'Simple' | 'Moderate' | 'Advanced';

  // What primitive types this template creates
  createsPrimitives: {
    resource: Partial<Resource>;
    integration: Partial<Integration>;
    workflow: Partial<WorkflowDefinition>;
    policy: Partial<Policy>;
  };

  // Template customization parameters
  parameters: TemplateParameter[];

  // Business value proposition
  businessValue: {
    timeSavings: string;
    accuracyImprovement: string;
    complianceAutomation: string;
    costReduction: string;
    riskMitigation: string;
  };

  // Implementation details
  estimatedSetupTime: string;
  prerequisites: string[];
  supportLevel: 'Community' | 'Professional' | 'Enterprise';
}

// Template category type for organization
export interface TemplateCategory {
  icon: string;
  description: string;
  count: number;
}

// Template generation result type
export interface TemplatePrimitiveResult {
  resource: Resource;
  integration: Integration;
  workflow: WorkflowDefinition;
  policy: Policy;
}

// API Service for Business Integration Templates using FERP GraphQL primitives
import { gql } from '@ferp/graphql-client';

const GET_BUSINESS_TEMPLATES = gql`
  query GetBusinessTemplates($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "WorkflowDefinition" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

const CREATE_TEMPLATE_PRIMITIVES = gql`
  mutation CreateTemplatePrimitives($inputs: [CreateDraftInput!]!) {
    createMultipleDrafts(inputs: $inputs)
  }
`;

export class BusinessIntegrationService {
  private apolloClient: any;

  constructor(apolloClient?: any) {
    this.apolloClient = apolloClient;
  }

  /**
   * Get all available business integration templates (stored as WorkflowDefinition primitives)
   */
  async getAvailableTemplates(tenantId: string): Promise<BusinessIntegrationTemplate[]> {
    if (!this.apolloClient) {
      throw new Error('Apollo client not provided to BusinessIntegrationService');
    }

    const { data } = await this.apolloClient.query({
      query: GET_BUSINESS_TEMPLATES,
      variables: { tenant: tenantId },
    });

    // Filter and transform WorkflowDefinition drafts that represent business integration templates
    return data?.listDrafts
      .filter((draft: any) => !draft.deleted && this.isBusinessIntegrationTemplate(draft))
      .map((draft: any) => this.transformDraftToBusinessTemplate(draft));
  }

  /**
   * Get templates by category
   */
  async getTemplatesByCategory(tenantId: string, category: string): Promise<BusinessIntegrationTemplate[]> {
    const allTemplates = await this.getAvailableTemplates(tenantId);
    return allTemplates.filter(template => template.category === category);
  }

  /**
   * Get template categories with counts
   */
  async getTemplateCategories(tenantId: string): Promise<Record<string, TemplateCategory>> {
    const allTemplates = await this.getAvailableTemplates(tenantId);

    const categoryCounts: Record<string, TemplateCategory> = {};

    allTemplates.forEach(template => {
      if (!categoryCounts[template.category]) {
        categoryCounts[template.category] = {
          icon: this.getCategoryIcon(template.category),
          description: this.getCategoryDescription(template.category),
          count: 0,
        };
      }
      categoryCounts[template.category]!.count++;
    });

    return categoryCounts;
  }

  /**
   * Get specific template by ID
   */
  async getTemplate(templateId: string, tenantId: string): Promise<BusinessIntegrationTemplate> {
    const allTemplates = await this.getAvailableTemplates(tenantId);
    const template = allTemplates.find(t => t.id === templateId);

    if (!template) {
      throw new Error(`Business integration template ${templateId} not found`);
    }

    return template;
  }

  /**
   * Instantiate a template with parameters - creates all 4 FERP primitives
   */
  async instantiateTemplate(
    templateId: string,
    parameters: Record<string, any>,
    tenantId: string,
    userId: string,
    sessionId: string
  ): Promise<TemplatePrimitiveResult> {
    if (!this.apolloClient) {
      throw new Error('Apollo client not provided to BusinessIntegrationService');
    }

    const template = await this.getTemplate(templateId, tenantId);

    // Generate all 4 primitive types based on template and parameters
    const primitives = this.generatePrimitivesFromTemplate(template, parameters, tenantId, userId);

    // Create all primitives in a single mutation
    const { data } = await this.apolloClient.mutate({
      mutation: CREATE_TEMPLATE_PRIMITIVES,
      variables: {
        inputs: [
          {
            tenant: tenantId,
            kind: 'Resource',
            key: primitives.resource.id,
            owner: userId,
            data: primitives.resource,
          },
          {
            tenant: tenantId,
            kind: 'Integration',
            key: primitives.integration.id,
            owner: userId,
            data: primitives.integration,
          },
          {
            tenant: tenantId,
            kind: 'WorkflowDefinition',
            key: primitives.workflow.id,
            owner: userId,
            data: primitives.workflow,
          },
          {
            tenant: tenantId,
            kind: 'Policy',
            key: primitives.policy.id,
            owner: userId,
            data: primitives.policy,
          },
        ],
      },
    });

    return primitives;
  }

  /**
   * Validate template parameters
   */
  async validateParameters(
    templateId: string,
    parameters: Record<string, any>,
    tenantId: string
  ): Promise<{ valid: boolean; errors: string[] }> {
    try {
      const template = await this.getTemplate(templateId, tenantId);
      const errors: string[] = [];

      // Validate required parameters
      template.parameters.forEach(param => {
        if (param.required && !parameters[param.name]) {
          errors.push(`Required parameter '${param.displayName}' is missing`);
        }

        if (param.validation && parameters[param.name]) {
          // Basic validation
          const value = parameters[param.name];
          if (param.validation.includes('url') && param.type === 'url') {
            try {
              new URL(value);
            } catch {
              errors.push(`Parameter '${param.displayName}' must be a valid URL`);
            }
          }
        }
      });

      return {
        valid: errors.length === 0,
        errors,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return {
        valid: false,
        errors: [`Failed to validate template: ${errorMessage}`],
      };
    }
  }

  private isBusinessIntegrationTemplate(draft: any): boolean {
    const data = draft.data || {};
    return data?.templateType === 'business_integration' ||
           data.category?.includes('Integration') ||
           data.category?.includes('Systems');
  }

  private transformDraftToBusinessTemplate(draft: any): BusinessIntegrationTemplate {
    const data = draft.data || {};
    return {
      id: draft.key,
      name: data.name || draft.key,
      businessFriendlyName: data.businessFriendlyName || data.displayName || data.name || draft.key,
      description: data.description || '',
      category: data.category || 'Custom',
      icon: data.icon || this.getCategoryIcon(data.category || 'Custom'),
      complexity: data.complexity || 'Moderate',
      createsPrimitives: data?.createsPrimitives || {
        resource: {},
        integration: {},
        workflow: {},
        policy: {},
      },
      parameters: data.parameters || [],
      businessValue: data.businessValue || {
        timeSavings: 'Automated workflow processing',
        accuracyImprovement: 'Reduced manual errors',
        complianceAutomation: 'Automated compliance checks',
        costReduction: 'Reduced operational overhead',
        riskMitigation: 'Improved error handling',
      },
      estimatedSetupTime: data.estimatedSetupTime || '30 minutes',
      prerequisites: data.prerequisites || [],
      supportLevel: data.supportLevel || 'Community',
    };
  }

  private generatePrimitivesFromTemplate(
    template: BusinessIntegrationTemplate,
    parameters: Record<string, any>,
    tenantId: string,
    userId: string
  ): TemplatePrimitiveResult {
    const baseId = `${template.id}-${tenantId}-${Date.now()}`;

    return {
      resource: {
        id: `${baseId}-resource`,
        name: `${template.businessFriendlyName} Resource`,
        resourceType: 'api',
        status: 'available',
        endpoint: parameters.endpoint || parameters.base_url || '',
        limits: { rateLimit: parameters.rateLimit || 100 },
        metadata: {
          templateId: template.id,
          createdFromTemplate: true,
          parameters,
        },
        ...template.createsPrimitives.resource,
      },
      integration: {
        id: `${baseId}-integration`,
        name: `${template.businessFriendlyName} Integration`,
        provider: template.id,
        connectionConfig: this.buildConnectionConfig(parameters),
        mappings: [],
        retryPolicy: { maxRetries: 3, backoffMs: 1000, timeoutMs: 30000 },
        metadata: {
          templateId: template.id,
          createdFromTemplate: true,
          parameters,
        },
        ...template.createsPrimitives.integration,
      },
      workflow: {
        id: `${baseId}-workflow`,
        name: `${template.businessFriendlyName} Workflow`,
        version: '1.0.0',
        initial_state: 'pending',
        states: this.buildWorkflowStates(template, parameters),
        metadata: {
          templateId: template.id,
          createdFromTemplate: true,
          parameters,
        },
        ...template.createsPrimitives.workflow,
      },
      policy: {
        id: `${baseId}-policy`,
        name: `${template.businessFriendlyName} Policy`,
        type: 'governance',
        policyType: 'governance',
        scope: 'workflow',
        rules: this.buildPolicyRules(template, parameters),
        implementation: { implType: 'inline' },
        auditLevel: 'summary',
        metadata: {
          templateId: template.id,
          createdFromTemplate: true,
          tenant: tenantId,
          parameters,
        },
        ...template.createsPrimitives.policy,
      },
    };
  }

  private buildConnectionConfig(parameters: Record<string, any>): any {
    return {
      baseUrl: parameters.endpoint || parameters.baseUrl || '',
      authType: parameters.authType || 'api_key',
      timeoutMs: parameters.timeoutMs || 30000,
      ...parameters,
    };
  }

  private buildWorkflowStates(template: BusinessIntegrationTemplate, parameters: Record<string, any>): any {
    return {
      pending: {
        on: {
          start: { to: 'processing', actions: ['initialize'] },
        },
      },
      processing: {
        on: {
          success: { to: 'completed', actions: ['finalize'] },
          error: { to: 'failed', actions: ['handle_error'] },
        },
      },
      completed: { on: {} },
      failed: { on: {} },
    };
  }

  private buildPolicyRules(template: BusinessIntegrationTemplate, parameters: Record<string, any>): any[] {
    return [
      {
        condition: 'workflow.status == "failed"',
        action: 'alert_operations',
        priority: 1,
      },
    ];
  }

  private getCategoryIcon(category: string): string {
    const icons: Record<string, string> = {
      'Payroll Systems': '💰',
      'Content Management': '📝',
      'Financial Processing': '💳',
      'Healthcare Systems': '🏥',
      'Custom': '🔧',
    };
    return icons[category] || '🔧';
  }

  private getCategoryDescription(category: string): string {
    const descriptions: Record<string, string> = {
      'Payroll Systems': 'Employee time tracking, payroll processing, and HR compliance',
      'Content Management': 'Website content, blogs, documentation, and publishing workflows',
      'Financial Processing': 'Payment processing, invoicing, and financial compliance',
      'Healthcare Systems': 'EHR integration, patient data, and HIPAA compliance',
      'Custom': 'Custom integrations and specialized business systems',
    };
    return descriptions[category] || 'Custom business integration templates';
  }
}